import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import classNames from 'classnames'
import { Formik } from 'formik'
import * as Yup from 'yup'

import CategoryItemPageHeader from 'common/components/onBoarding/Common'
import { phoneNumberRegex } from 'common/components/utils/InputUtils'

import useCommandRequest from 'common/hooks/useCommandRequest'

import { Alert, Col, Icon, Input, InputMask, Radio, Row } from 'common/components'
import { useBreakPoints } from 'common/hooks'

import { BaseComponentProps } from '../models'

export interface PersonalDetailsProps {
  personalDetailValues: any
  submitHandler: (values: any) => Promise<void>
  next: boolean
  numberOfQuestions: number
  currentQuestion: number
}

const PersonalDetails: React.FC<BaseComponentProps<PersonalDetailsProps>> = (props) => {
  const { bindSubmitFormHandler } = props
  const { t: translate } = useTranslation()
  const [sendCommand] = useCommandRequest()
  const { isLg } = useBreakPoints()

  useEffect(() => {
    props.setIsFormValid(formValidator.isValidSync(props.state))
  }, [])

  const onSubmit = async (values: any) => {
    const result = await sendCommand(() => props.state.submitHandler(values))
    if (result.completed) {
      {
        props.state.next ? props.goNext() : props.finish()
      }
    } else {
      props.setIsSubmiting(false)
    }
  }

  return (
    <Formik
      enableReinitialize={true}
      validationSchema={formValidator}
      initialValues={props.state.personalDetailValues}
      onSubmit={onSubmit}
      validate={(values) => {
        props.hasDirtyData(true)
        props.setIsFormValid(formValidator.isValidSync(values))
        return {}
      }}
    >
      {(formik) => {
        const { handleSubmit, submitForm } = formik
        bindSubmitFormHandler(submitForm)
        return (
          <form onSubmit={handleSubmit} autoComplete="off">
            <Row justify={window.innerWidth < 767 ? 'center' : 'start'} className="mt-32">
              <Col xs={24} sm={23} md={{ span: 15, offset: 2 }} lg={{ span: 12, offset: 2 }}>
                <CategoryItemPageHeader
                  title={translate('ee-signup.personal-details.header')}
                  numberOfQuestions={props.state.numberOfQuestions}
                  currentQuestion={props.state.currentQuestion}
                  question={'Enter your personal email and phone number.'}
                />
              </Col>
            </Row>

            <Row className="form__row" justify={window.innerWidth < 767 ? 'center' : 'start'}>
              <Col xs={23} sm={23} md={{ span: 15, offset: 2 }} lg={{ span: 12, offset: 2 }}>
                <div className={classNames({ 'pl-8': isLg })}>
                  <Alert
                    type="info"
                    showIcon
                    message={''}
                    description={
                      'Your personal email is needed in case you get locked out of your account and need help getting back in. Your recovery email cannot be the same as your company email.'
                    }
                  />
                </div>
              </Col>
            </Row>

            <Row className="form__row" justify={window.innerWidth < 767 ? 'center' : 'start'}>
              <Col xs={24} sm={23} md={{ span: 15, offset: 2 }} lg={{ span: 12, offset: 2 }}>
                <Input
                  label={translate('ee-signup.personal-details.email-label')}
                  name="personalEmail"
                  placeholder="name@company.com"
                  suffix={<Icon type="mail" style={{ color: '#ADB5C0' }} />}
                />
              </Col>
            </Row>

            <Row className="form__row" justify={window.innerWidth < 767 ? 'center' : 'start'}>
              <Col xs={24} sm={23} md={{ span: 15, offset: 2 }} lg={{ span: 12, offset: 2 }}>
                <InputMask
                  label={translate('ee-signup.personal-details.phone-label')}
                  name="phoneNumber"
                  mask="(000) 000-0000"
                  placeholder="(555) 123-4567"
                  suffix={<Icon className="far fa-phone" style={{ color: '#ADB5C0' }} />}
                />
              </Col>
            </Row>

            <Row className="form__row" justify={window.innerWidth < 767 ? 'center' : 'start'}>
              <Col xs={23} sm={23} md={{ span: 15, offset: 2 }} lg={{ span: 12, offset: 2 }}>
                <label>Do you want to receive SMS updates from PeopleKeep about your benefit?</label>
                <Radio.Group name="receiveSms">
                  <Radio value={true}>Yes</Radio>
                  <Radio value={false}>No</Radio>
                </Radio.Group>
              </Col>
            </Row>
          </form>
        )
      }}
    </Formik>
  )
}

const formValidator = Yup.object({
  personalEmail: Yup.string().nullable().email('Please enter a valid email').required('Please enter an email.'),

  phoneNumber: Yup.string().nullable().required('Required').matches(phoneNumberRegex, 'Phone number is not correct.'),

  receiveSms: Yup.boolean().nullable().required('Required'),
})

export default PersonalDetails
